import { ProgramContainer } from "src/entities/formTemplateDataType";
import ReactGA from "react-ga4";
import { slugifySectionName } from "./URLUtils";
import { getPublicSecurePage } from "./ElementUtils";
import SessionStorageService from "../services/SessionStorageService";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

let lastTrackedPath = "";
let lastTrackedAt = 0;

const sendPageViewViaGaTrackingId = (
  trackingId: string,
  payload: {
    page_location: string;
    page_path: string;
    page_title: string;
    page_referrer: string;
    user_type: string;
    email: string;
    userId: string;
  },
) => {
  ReactGA.send({
    hitType: "pageview",
    page: payload.page_path,
    title: payload.page_title,
  });

  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", "page_view", payload);
  }
};

const TrackPageView = (programContainer: ProgramContainer) => {
  // if(programContainer?.CategoryId?.toLocaleLowerCase() === "oer") {
  //   return;
  // }

  const currentPath = window.location.pathname + window.location.search;
  const now = Date.now();
  if (lastTrackedPath === currentPath && now - lastTrackedAt < 2000) {
    return;
  }
  if (sessionStorage.getItem("gaUploadPause") === "true") {
    console.log('[TrackPageView] Skipping - gaUploadPause is true');
    return;
  }
  if (programContainer?.sendPageDataToGA === false) {
    console.log('[TrackPageView] Skipping - sendPageDataToGA is disabled');
    return;
  }
  lastTrackedPath = currentPath;
  lastTrackedAt = now;

  const title = getTitleFromPath();
  const storedPreviousUrl = sessionStorage.getItem("previousUrl");
  const shouldForceTrackAfterCxpRegistration =
    localStorage.getItem("forceTrackAfterCxpRegistration") === "true";
  
  // Avoid tracking when returning from OAuth flow (CXP signin) with code parameter
  const hasOAuthCode = window.location.search.includes("code=");
  if (hasOAuthCode && !shouldForceTrackAfterCxpRegistration) {
    // Mark that OAuth callback was seen so next render can suppress tracking on Public Secure.
    sessionStorage.setItem("oauthReturnInProgress", "true");
    console.log('[TrackPageView] Skipping - OAuth callback in progress (code parameter present)');
    // Don't restore previousUrl from localStorage yet - wait until we're on the final destination page
    return;
  } else if (hasOAuthCode && shouldForceTrackAfterCxpRegistration) {
    sessionStorage.setItem("oauthReturnInProgress", "true");
    console.log('[TrackPageView] OAuth callback detected, continuing tracking due to forceTrackAfterCxpRegistration');
  }
  
  // Check if we have a previousUrl stored in localStorage from before OAuth redirect
  // Only restore it now (after OAuth completed and we're on the actual page to track)
  const previousUrlBeforeOAuth = localStorage.getItem("previousUrlBeforeOAuth");
  if (previousUrlBeforeOAuth) {
    console.log('[TrackPageView] Found previousUrl from OAuth flow:', previousUrlBeforeOAuth);
    // Move it to sessionStorage for normal handling
    sessionStorage.setItem("previousUrl", previousUrlBeforeOAuth);
    // Clean up localStorage
    localStorage.removeItem("previousUrlBeforeOAuth");
  }
  
  // Re-read storedPreviousUrl in case we just restored it from localStorage
  const previousUrlToUse = sessionStorage.getItem("previousUrl") || storedPreviousUrl;
  
  // Determine the actual previous page URL
  // Priority:
  // 1. Use storedPreviousUrl if available (indicates internal navigation within form)
  // 2. Otherwise, check document.referrer for external CXP sources (like /web/dashboard)
  // 3. Fall back to document.referrer if nothing else available
  const isSignedIn = !!(
    programContainer.Profile?.token ||
    programContainer.Profile?.userId ||
    sessionStorage.getItem("userProfile")
  );
  const hasAcceptedTerms = !!(
    programContainer.Profile?.agreedToTermsOfUseOn ||
    (programContainer as any)?.Entity?.accepted === true
  );
  const authReturnSource = localStorage.getItem("authReturnSource");
  const shouldTrackPublicSecureAfterOktaReturn =
    localStorage.getItem("trackPublicSecureAfterSignin") === "true" &&
    authReturnSource === "publicSecureButton";

  if (
    localStorage.getItem("trackPublicSecureAfterSignin") === "true" &&
    authReturnSource !== "publicSecureButton"
  ) {
    localStorage.removeItem("trackPublicSecureAfterSignin");
  }

  const publicSecureSection = getPublicSecurePage(programContainer.ListOfFormSections);
  let previousUrl = previousUrlToUse;

  // If the stored previousUrl points to a section that is skipped from workflow,
  // do not use it as previousUrl.
  // Clear it so we fall through to the document.referrer check below (e.g. /web/dashboard).
  if (
    previousUrlToUse &&
    isPreviousUrlSkippedFromWorkflow(previousUrlToUse, programContainer, {
      hasAcceptedTerms,
      isSignedIn,
      shouldTrackPublicSecureAfterOktaReturn,
      publicSecureSection,
    })
  ) {
    console.log('[TrackPageView] Stored previousUrl points to a skipped section, ignoring:', previousUrlToUse);
      previousUrl = "";
  }

  // Only check document.referrer if we don't have a stored previousUrl from internal navigation
  if (!previousUrl && document.referrer) {
    try {
      const referrerUrl = new URL(document.referrer);
      const currentUrl = new URL(window.location.href);
      
      // Check if referrer is from a different app path (not /on-form/)
      const isExternalReferrer = !referrerUrl.pathname.includes('/on-form/');
      
      // If coming from external CXP source (like /web/dashboard), use it
      if (isExternalReferrer && referrerUrl.origin === currentUrl.origin) {
        previousUrl = document.referrer;
        console.log('[TrackPageView] Using external CXP referrer:', previousUrl);
      }
    } catch (e) {
      // Invalid URL, ignore
    }
  }
  
  const currentSectionIndex = getSectionIndexFromPath(programContainer);
  const publicSecureIndex = getPublicSecureButtonSectionIndex(programContainer);
  const isReturningFromOAuth =
    sessionStorage.getItem("oauthReturnInProgress") === "true" ||
    !!previousUrlToUse?.includes("?code") ||
    !!storedPreviousUrl?.includes("?code");

  // On the first page after OAuth callback, never track the Public Secure section.
  if (
    isReturningFromOAuth &&
    Number.isFinite(currentSectionIndex) &&
    Number.isFinite(publicSecureIndex) &&
    currentSectionIndex >= 0 &&
    publicSecureIndex >= 0 &&
    currentSectionIndex === publicSecureIndex
  ) {
    sessionStorage.removeItem("oauthReturnInProgress");
    console.log('[TrackPageView] Skipping - returned from OAuth and currently at Public Secure section');
    return;
  }

  if (isReturningFromOAuth && currentSectionIndex !== publicSecureIndex) {
    sessionStorage.removeItem("oauthReturnInProgress");
  }

  const shouldSkipFirstSectionFromDraft =
    programContainer?.saveLaterEnabled === true &&
    currentSectionIndex === 0 &&
    /\/web\/(?:fr\/)?drafts\/\d+(?:\/|$)/.test(document.referrer || "");

  // Avoid tracking the first section when coming back from a draft and Save Later is enabled.
  if (shouldSkipFirstSectionFromDraft) {
    return;
  }
  
  const isReturningBeforePublicSecure =
    !!storedPreviousUrl?.includes("?code") &&
    Number.isFinite(currentSectionIndex) &&
    Number.isFinite(publicSecureIndex) &&
    currentSectionIndex >= 0 &&
    publicSecureIndex >= 0 &&
    currentSectionIndex <= publicSecureIndex;

  // Avoid double-tracking when returning from Public Secure and we're still before the auth page.
  if (isReturningBeforePublicSecure) {
    if (shouldForceTrackAfterCxpRegistration) {
      sessionStorage.removeItem("previousUrl");
    } else {
    sessionStorage.removeItem("previousUrl");
    return;
    }
  }

  // Avoid tracking if user is trying to access a secure page without a PS token
  if (
    currentSectionIndex >= 0 &&
    publicSecureIndex >= 0 &&
    currentSectionIndex > publicSecureIndex &&
    !SessionStorageService.getPSToken() &&
    !isSignedIn &&
    programContainer.AuthRequired
  ) {
    return;
  }

  // Avoid tracking sections marked with SkipPageViewTracking if user has already agreed to Terms of Use
  if (
    currentSectionIndex >= 0 &&
    currentSectionIndex < programContainer.ListOfFormSections.length
  ) {
    const currentSection =
      programContainer.ListOfFormSections[currentSectionIndex];
    const isPublicSecureSection = publicSecureSection && currentSection === publicSecureSection;
    const shouldSkipCurrentSectionFromWorkflow = isSectionSkippedFromWorkflow({
      section: currentSection,
      isPublicSecureSection,
      isSignedIn,
      hasAcceptedTerms,
      shouldTrackPublicSecureAfterOktaReturn,
    });
    
    console.log('[TrackPageView] Public secure check:', {
      currentSectionTitle: currentSection.ENUTitle,
      publicSecureSectionTitle: publicSecureSection?.ENUTitle,
      isPublicSecureSection,
      isSignedIn,
      authReturnSource,
      shouldTrackPublicSecureAfterOktaReturn,
      hasToken: !!programContainer.Profile?.token,
      hasUserId: !!programContainer.Profile?.userId,
      hasSessionUserProfile: !!sessionStorage.getItem("userProfile"),
      hasAcceptedTerms,
      skipPageViewTracking: !!currentSection?.SkipPageViewTracking,
      isVisible: currentSection?.Visible !== false,
      shouldSkipCurrentSectionFromWorkflow,
    });
    
    if (shouldSkipCurrentSectionFromWorkflow) {
      if (shouldForceTrackAfterCxpRegistration) {
        console.log('[TrackPageView] Force-tracking after CXP registration for section:', currentSection.ENUTitle);
      } else {
      if (isPublicSecureSection) {
        localStorage.removeItem("trackPublicSecureAfterSignin");
        localStorage.removeItem("authReturnSource");
      }
      console.log(
        '[TrackPageView] Skipping tracking for section "' +
          currentSection.ENUTitle +
          '" - section is skipped from workflow',
      );
      return;
      }
    }

    if (
      isPublicSecureSection &&
      localStorage.getItem("trackPublicSecureAfterSignin") === "true" &&
      authReturnSource !== "publicSecureButton"
    ) {
      localStorage.removeItem("trackPublicSecureAfterSignin");
    }

    if (isPublicSecureSection && shouldTrackPublicSecureAfterOktaReturn) {
      localStorage.removeItem("trackPublicSecureAfterSignin");
      localStorage.removeItem("authReturnSource");
    }
  }

  let trackingURL = window.location.href;
  // Remove sensitive query parameters from tracking URL
  const urlObj = new URL(trackingURL);
  urlObj.searchParams.delete("code");
  urlObj.searchParams.delete("state");
  trackingURL = urlObj.toString();

  const isCxpUser = !!(
    programContainer.Profile?.token || sessionStorage.getItem("userProfile")
  );
  const gaTrackingId = programContainer.GATrackingID?.trim();
  const sessionUserProfile = isCxpUser
    ? JSON.parse(sessionStorage.getItem("userProfile") || "{}")
    : {};

  const finalReferrer = previousUrl || document.referrer || previousUrlToUse || "";
  const trackingFingerprint = `${window.location.pathname}${window.location.search}|${finalReferrer}`;
  const lastTrackingFingerprint = sessionStorage.getItem("lastTrackingFingerprint");
  const lastTrackingAt = Number(sessionStorage.getItem("lastTrackingAt") || "0");

  if (
    lastTrackingFingerprint === trackingFingerprint &&
    now - lastTrackingAt < 3000
  ) {
    console.log('[TrackPageView] Skipping duplicate pageview payload for fingerprint:', trackingFingerprint);
    return;
  }

  sessionStorage.setItem("lastTrackingFingerprint", trackingFingerprint);
  sessionStorage.setItem("lastTrackingAt", now.toString());

  console.log(
    "[TrackPageView] called",
    window.location.pathname,
    "isCxpUser:",
    isCxpUser,
    "finalReferrer:",
    finalReferrer
  );

  const pageViewPayload = {
    event: "cxp.pageview",
    page_location: trackingURL,
    page_path: window.location.pathname,
    page_title: title,
    page_referrer: finalReferrer,
    user_type: isCxpUser ? "signed in" : "guest",
    email: isCxpUser
      ? sessionUserProfile.email || ""
      : "",
    userId: isCxpUser
      ? sessionUserProfile.userId || ""
      : "",
  };

  if (gaTrackingId) {
    sendPageViewViaGaTrackingId(gaTrackingId, pageViewPayload);
  } else {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(pageViewPayload);
  }

  if (shouldForceTrackAfterCxpRegistration) {
    localStorage.removeItem("forceTrackAfterCxpRegistration");
  }
  sessionStorage.removeItem("previousUrl");
  sessionStorage.removeItem("languageChange");
};

const getSectionIndexFromPath = (programContainer) => {
  const path = window.location.pathname;
  if (!path) return -1; // return -1 if path is empty

  const segments = path.split("/").filter(Boolean); // removes empty strings
  const lastSegment = segments[segments.length - 1];
  if (!programContainer) return -1;

  const normalizeSlug = (value?: string) =>
    slugifySectionName({ ENUTitle: value || "" } as any);

  const sectionIndex = programContainer.ListOfFormSections.findIndex(
    (section) => {
      const candidates = [section.ENUTitle, section.FRATitle, section.pageName];
      return candidates.some(
        (candidate) => normalizeSlug(candidate) === lastSegment,
      );
    },
  );

  return sectionIndex;
};

const getPublicSecureButtonSectionIndex = (
  programContainer: ProgramContainer,
) => {
  const publicSecureSection = programContainer
    ? getPublicSecurePage(programContainer.ListOfFormSections)
    : null;
  if (!publicSecureSection) return -1;
  return programContainer.ListOfFormSections.indexOf(publicSecureSection);
};

const getTitleFromPath = () => {
  const path = window.location.pathname;
  if (!path) return "Default Title";

  const segments = path.split("/").filter(Boolean); // removes empty strings
  const lastSegment = segments[segments.length - 1] || "home";

  return (
    lastSegment.charAt(0).toUpperCase() +
    lastSegment.slice(1).replace(/-/g, " ")
  );
};

/**
 * Given a full URL, returns the matching form section from the programContainer
 * by matching the URL's last path segment against section title slugs / pageName.
 * Returns undefined if no match is found.
 */
const getSectionFromUrl = (url: string, programContainer: ProgramContainer) => {
  if (!url || !programContainer?.ListOfFormSections) return undefined;
  try {
    const urlObj = new URL(url);
    const segments = urlObj.pathname.split("/").filter(Boolean);
    const lastSegment = segments[segments.length - 1];
    if (!lastSegment) return undefined;

    const normalizeSlug = (value?: string) =>
      slugifySectionName({ ENUTitle: value || "" } as any);

    return programContainer.ListOfFormSections.find((section) => {
      const candidates = [section.ENUTitle, section.FRATitle, section.pageName];
      return candidates.some((c) => normalizeSlug(c) === lastSegment);
    });
  } catch {
    return undefined;
  }
};

const isSectionSkippedFromWorkflow = ({
  section,
  isPublicSecureSection,
  isSignedIn,
  hasAcceptedTerms,
  shouldTrackPublicSecureAfterOktaReturn,
}: {
  section: any;
  isPublicSecureSection: boolean;
  isSignedIn: boolean;
  hasAcceptedTerms: boolean;
  shouldTrackPublicSecureAfterOktaReturn: boolean;
}) => {
  if (!section) return false;
  if (section?.Visible === false) return true;
  if (isPublicSecureSection && isSignedIn && !shouldTrackPublicSecureAfterOktaReturn) {
    return true;
  }
  if (section?.SkipPageViewTracking && hasAcceptedTerms) {
    return true;
  }
  return false;
};

const isPreviousUrlSkippedFromWorkflow = (
  url: string,
  programContainer: ProgramContainer,
  {
    hasAcceptedTerms,
    isSignedIn,
    shouldTrackPublicSecureAfterOktaReturn,
    publicSecureSection,
  }: {
    hasAcceptedTerms: boolean;
    isSignedIn: boolean;
    shouldTrackPublicSecureAfterOktaReturn: boolean;
    publicSecureSection: any;
  },
) => {
  const prevSection = getSectionFromUrl(url, programContainer);
  const isPublicSecureSection = !!(publicSecureSection && prevSection === publicSecureSection);
  return isSectionSkippedFromWorkflow({
    section: prevSection,
    isPublicSecureSection,
    isSignedIn,
    hasAcceptedTerms,
    shouldTrackPublicSecureAfterOktaReturn,
  });
};

export default TrackPageView;
